import type { LDFlagSet } from '@nx/launch-darkly-service';
import { neo4jVersionUtil } from '@nx/neo4j-version-utils';
import type {
  Instance,
  MetricAggregation,
  MetricWithAggregations,
  MetricsRequest,
  MetricsRequestType,
  MetricsResponse,
  MetricsResult,
  MetricsWithAZGroupingRequest,
  MetricsWithAggregationsRequest,
  SeriesSummary,
} from '@nx/state';
import { TIER } from '@nx/state';
import { isNonEmptyString, isNotNullish } from '@nx/stdlib';

import { metricKey } from '../../shared/utils';
import { AuraDatabaseCharts } from '../charts/database-charts';
import type { ChartType, ChartWidgetPartialProps } from '../charts/fullstack-chart-props';
import { AuraInstanceCharts } from '../charts/instance-charts';
import { InstanceDashboardCharts } from '../charts/instance-dashboard-charts';
import { mapAuraMetricsWithAZGrouping, mapAuraMetricsWithAggregations, mapMetrics } from '../charts/mappers';
import { AuraSystemCharts } from '../charts/system-charts';
import type { MultiChartProps } from '../charts/types';
import { nonNullable } from './typeguards';
import { MetricSource } from './types';
import { sortedAggregations } from './utils';

export class AuraMetricSource extends MetricSource {
  constructor(
    public useAggregations: boolean,
    public useAZGrouping: boolean,
    public showLeaderOnly: boolean,
    public instance: Instance,
    database?: string,
  ) {
    super(instance.projectId, instance.id, instance.name, database);
  }

  getPills(chartProps: MultiChartProps, metrics: MetricsResult['metrics']): SeriesSummary[] {
    const chartPropsList = Array.isArray(chartProps) ? chartProps : [chartProps];
    const additionalSeriesNames = chartPropsList.flatMap((chartProp) => {
      return chartProp.additionalMetric && !this.useAZGrouping
        ? [
            {
              id: chartProp.subtitle ?? chartProp.title,
              name: chartProp.seriesName ?? chartProp.title,
              displayName: chartProp.seriesName ?? chartProp.title,
            },
            {
              id: chartProp.additionalMetric.subtitle ?? chartProp.additionalMetric.title,
              name: chartProp.additionalMetric.seriesName ?? chartProp.additionalMetric.title,
              displayName: chartProp.additionalMetric.seriesName ?? chartProp.title,
            },
          ]
        : [];
    });
    const pills = [...additionalSeriesNames];
    const [chartProp] = chartPropsList;
    const dataSeries = chartProp && metrics?.[metricKey(chartProp)]?.dataSeries;
    if (this.useAZGrouping && chartProp && dataSeries && !additionalSeriesNames.length) {
      pills.push(
        ...Object.keys(dataSeries)
          .filter((az) => !sortedAggregations.some((sa) => sa.id === az))
          .filter((az) => (this.showLeaderOnly ? !az.includes('Secondaries') : true))
          .map((az) => ({
            id: az,
            name: az,
            displayName: az,
          })),
      );
      pills.sort((pill1, pill2) => pill1.displayName.localeCompare(pill2.displayName));
    } else {
      pills.push(...sortedAggregations);
    }
    return pills;
  }

  getCharts(flags: LDFlagSet): Record<ChartType, MultiChartProps[]> {
    function tierFilter(chartProps: ChartWidgetPartialProps, tier: TIER | undefined): boolean {
      return (
        isNotNullish(tier) &&
        (!chartProps.enabledFor || chartProps.enabledFor.length === 0 || chartProps.enabledFor.includes(tier))
      );
    }

    function featureFlagFilter(chartProps: ChartWidgetPartialProps, lDFlags: LDFlagSet): boolean {
      return (
        !chartProps.controlledBy ||
        chartProps.controlledBy.length === 0 ||
        chartProps.controlledBy.reduce((result, cv) => {
          const flagValue = Boolean(lDFlags[cv.name]);
          return result && ((cv.state === 'ON' && flagValue) || (cv.state === 'OFF' && !flagValue));
        }, true)
      );
    }

    function multidbFilter(chartProps: ChartWidgetPartialProps, isMultiDb: boolean | undefined): boolean {
      if (isNotNullish(isMultiDb) && isMultiDb) {
        return (
          chartProps.metricName !== 'neo4j_aura_storage_limit' &&
          chartProps.referenceMetricConfig?.name !== 'neo4j_aura_storage_limit' &&
          chartProps.additionalMetric?.metricName !== 'neo4j_aura_storage_limit'
        );
      }
      return true;
    }

    const instanceFulfillsVersionRequirement = (chartProps: MultiChartProps) => {
      const minVersion = (Array.isArray(chartProps) ? chartProps[0] : chartProps)?.minimumRequiredDatabaseVersion;
      if (isNonEmptyString(minVersion) && isNotNullish(this.instance) && this.instance.desiredSettings.version) {
        return neo4jVersionUtil.gte(this.instance.desiredSettings.version, minVersion);
      }
      return true;
    };

    const isMultiDb = this.instance.capabilities.multi_database.enabled;

    const applyFiltersTo = (chartProps: MultiChartProps[]): MultiChartProps[] => {
      return chartProps
        .filter((cp) => {
          if (Array.isArray(cp)) {
            return true;
          }
          return (
            tierFilter(cp, this.instance.tier) &&
            featureFlagFilter(cp, flags) &&
            multidbFilter(cp, isMultiDb) &&
            instanceFulfillsVersionRequirement(cp)
          );
        })
        .map((cp) => {
          if (Array.isArray(cp)) {
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
            return (cp as ChartWidgetPartialProps[]).filter(
              (p) => tierFilter(p, this.instance.tier) && featureFlagFilter(p, flags) && multidbFilter(p, isMultiDb),
            );
          }
          return cp;
        })
        .map((cp) => {
          if (!Array.isArray(cp) && this.useAZGrouping && cp.additionalMetric !== undefined) {
            const additionalMetric = Object.assign({}, cp.additionalMetric);
            return [cp, additionalMetric];
          }

          return cp;
        });
    };

    return {
      HOST: applyFiltersTo(AuraSystemCharts),
      INSTANCE: applyFiltersTo(AuraInstanceCharts),
      DB: applyFiltersTo(AuraDatabaseCharts),
      DASHBOARD: applyFiltersTo(InstanceDashboardCharts),
    };
  }

  isCmiAvailable(): boolean {
    return (
      this.instance.tier === TIER.ENTERPRISE || this.instance.tier === TIER.MTE || this.instance.tier === TIER.AURA_DSE
    );
  }

  createMetricsRequest(
    startTime: number,
    endTime: number,
    chartType: ChartType,
    chartProps: MultiChartProps[],
  ): {
    metricsRequest: MetricsRequest | MetricsWithAggregationsRequest | MetricsWithAZGroupingRequest;
    type: MetricsRequestType;
  } {
    const getMetricNames = (): MetricWithAggregations[] => {
      const allAggregations: MetricAggregation[] = ['MAX', 'AVG', 'MIN'];
      const maxOnlyAggregations: MetricAggregation[] = ['MAX'];
      const names = chartProps
        .flat()
        .flatMap((c) => [
          {
            metric: c.metricName,
            aggregations: c.aggregations ? c.aggregations : allAggregations,
          },
          c.referenceMetricConfig
            ? {
                metric: c.referenceMetricConfig.name,
                aggregations: c.referenceMetricConfig.aggregation
                  ? [c.referenceMetricConfig.aggregation]
                  : maxOnlyAggregations,
              }
            : undefined,
          c.additionalMetric && !this.useAZGrouping
            ? {
                metric: c.additionalMetric.metricName,
                aggregations: c.aggregations ? c.aggregations : allAggregations,
              }
            : undefined,
          this.useAZGrouping && this.showLeaderOnly && c.transformerMetricConfig
            ? {
                metric: c.transformerMetricConfig.name,
                aggregations: c.transformerMetricConfig.aggregation
                  ? [c.transformerMetricConfig.aggregation]
                  : allAggregations,
              }
            : undefined,
        ])
        .filter(nonNullable);
      return names.filter((metric, pos) => names.findIndex((item) => item.metric === metric.metric) === pos);
    };

    const metricNames = getMetricNames();

    const dbName = chartType === 'DB' ? this.database : undefined;
    if (this.useAggregations) {
      return {
        metricsRequest: {
          dbName,
          tenantId: this.tenantId,
          dbmsId: this.dbmsId,
          metrics: metricNames,
          startTime: startTime,
          endTime: endTime,
        },
        type: 'metricsWithAggregations',
      };
    } else if (this.useAZGrouping) {
      return {
        metricsRequest: {
          dbName,
          tenantId: this.tenantId,
          dbmsId: this.dbmsId,
          name: metricNames.map((mn) => mn.metric),
          startTime: startTime,
          endTime: endTime,
        },
        type: 'metricsWithAZGrouping',
      };
    }
    return {
      metricsRequest: {
        dbName,
        tenantId: this.tenantId,
        dbmsId: this.dbmsId,
        name: metricNames.map((mn) => mn.metric),
        startTime: startTime,
        endTime: endTime,
      },
      type: 'metrics',
    };
  }

  mapMetrics(
    metrics: MetricsResponse,
    chartProps: MultiChartProps[],
    seriesNameMapper?: (props: ChartWidgetPartialProps) => string,
  ): MetricsResult['metrics'] | null {
    if (this.useAggregations) {
      return mapAuraMetricsWithAggregations(metrics, chartProps.flat());
    } else if (this.useAZGrouping) {
      return mapAuraMetricsWithAZGrouping(metrics, chartProps.flat());
    }
    return mapMetrics(metrics, chartProps.flat(), this.name, seriesNameMapper);
  }
}
